import React, { useContext, useState, useRef, useEffect } from "react";
import ConversationsSidebarContainer from "@/components/chat/ConversationsSidebarContainer";
import ChatAreaChild from "@/components/chat/ChatAreaChild";
import { useGetChatRoomsQuery } from "@/api/chatApi/chat";
import { ChatContext, ChatContextInterface } from "@/components/MSRContext";
import Global from "@/globals/globals";
import type { ChatRoom } from "@/types/chat.types";
import generateRandomString from "../MSRP/utilities/generators";
import { createEmptyMultiPart, MessagePart } from "../utils/multiPartBuilder";
import { MyRequest } from "../models/MyRequest";
import { Message } from "../models/Message";
import { FileMessage } from "../models/FileMessage";
import Navbar from "../components/navbar";
import AppSidebar from "../components/AppSidebar";
import { SidebarProvider } from "../context/SidebarContext";
import ConnectionStatusBanner from "@/components/ConnectionStatusBanner";
import { isAgentUserType } from "@/utils/userType";
import { isSessionKeyReopened, isSessionKeyDestroyed, isSameSessionKey } from "@/utils/sessionReopenTracker";

/**
 * ChatDashboard - New unified chat dashboard
 *
 * This page shows the chat list (sidebar) and chat area side-by-side
 * using parent-child architecture with NO URL navigation.
 *
 * Architecture:
 * ChatDashboard (Page)
 * └── ConversationsSidebarContainer (PARENT)
 *     ├── Sidebar with room list (always visible)
 *     └── ChatAreaChild (CHILD - shows selected room)
 */

const ChatDashboard: React.FC = () => {
  // Get MSRP wrapper, context functions, and rooms from WebSocket
  const context = useContext(ChatContext) as ChatContextInterface;
  const { wrapper, handleaddServerRequest, updateAgentList, rooms: contextRooms, controller, handlemarkAsRead } = context;

  useEffect(() => {
    if (updateAgentList) {
      updateAgentList("all-status");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrapper]); // Re-run when wrapper is ready
  // Track selected room for MSRP operations
  const [selectedRoom, setSelectedRoom] = useState<ChatRoom | null>(null);

  // Track if user is at bottom of selected room's message list (Telegram-style)
  const [isUserAtBottom, setIsUserAtBottom] = useState(true);

  // Ref to store the clear selection function from ConversationsSidebarContainer
  const clearSelectionRef = useRef<(() => void) | null>(null);

  // Ref to store the select function from ConversationsSidebarContainer
  const selectRoomRef = useRef<((room: ChatRoom) => void) | null>(null);

  // Fetch rooms via RTK Query (include=messages for instant message display)
  const { data, isLoading, refetch } = useGetChatRoomsQuery({
    personal: true,
    "chat-type": "any",
    "session-status": "both",
    include: "messages",
    message_limit: 25,
  });

  // Local state for rooms - allows UI updates without waiting for API
  const [localRooms, setLocalRooms] = useState<ChatRoom[]>([]);

  const toUnixSeconds = (value: unknown): number | undefined => {
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? undefined : Math.floor(value.getTime() / 1000);
    }

    if (typeof value === "number") {
      return value > 9_999_999_999 ? Math.floor(value / 1000) : value;
    }

    if (typeof value === "string" && value.trim()) {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? undefined : Math.floor(parsed.getTime() / 1000);
    }

    return undefined;
  };

  const getMessagePreview = (message: any): string => {
    if (!message) return "";

    if (typeof message.text === "string" && message.text.trim()) {
      return message.text;
    }

    if (typeof message.message === "string" && message.message.trim()) {
      return message.message;
    }

    if (typeof message.fileName === "string" && message.fileName.trim()) {
      return `📎 ${message.fileName}`;
    }

    return "";
  };

  const mergeRoomsWithControllerState = React.useCallback(
    (apiRooms: ChatRoom[]) => {
      const controllerRoomsById = new Map(controller.chatRooms.map((room) => [room.chatRoomId, room]));

      const applyLiveReopenState = (room: ChatRoom): ChatRoom => {
        if (isSessionKeyDestroyed(room.session_key)) {
          return { ...room, session_status: "inactive" };
        }
        if (isSessionKeyReopened(room.session_key)) {
          return { ...room, session_status: "active" };
        }
        return room;
      };

      return apiRooms.map<ChatRoom>((apiRoom) => {
        const controllerRoom = controllerRoomsById.get(apiRoom.session_key);
        if (!controllerRoom) {
          return applyLiveReopenState(apiRoom);
        }

        const lastControllerMessage = controllerRoom.messages[controllerRoom.messages.length - 1];
        const controllerMessageTime =
          toUnixSeconds(lastControllerMessage?.arrivalTime) ??
          toUnixSeconds(lastControllerMessage?.deliveredTime) ??
          toUnixSeconds(lastControllerMessage?.sentTime);
        const apiMessageTime = apiRoom.last_message_at;

        const controllerIsFresher =
          controllerMessageTime !== undefined &&
          (apiMessageTime === undefined || controllerMessageTime >= apiMessageTime);

        const preview = controllerIsFresher ? getMessagePreview(lastControllerMessage) : "";
        const latestTime = controllerIsFresher ? controllerMessageTime : apiMessageTime;

        return applyLiveReopenState({
          ...apiRoom,
          session_status: (controllerRoom.isSessionActive ? "active" : "inactive") as "active" | "inactive",
          unread_count: controllerRoom.unreadMessageCount,
          last_message: preview
            ? {
                ...(apiRoom.last_message || {}),
                text: preview,
                content_type: null,
              }
            : apiRoom.last_message,
          last_message_at: latestTime,
        });
      });
    },
    [controller]
  );

  // Track rooms that have been marked as read (seen event sent)
  const [markedAsReadRooms, setMarkedAsReadRooms] = useState<Set<string>>(new Set());

  // Sync localRooms with API data
  useEffect(() => {
    if (data?.chatRooms) {
      const mergedRooms = mergeRoomsWithControllerState(data.chatRooms);

      // Clean up markedAsReadRooms - remove rooms where API already shows 0
      const roomsToRemove = mergedRooms
        .filter((r) => r.unread_count === 0 && markedAsReadRooms.has(r.session_key))
        .map((r) => r.session_key);

      if (roomsToRemove.length > 0) {
        setMarkedAsReadRooms(prev => {
          const newSet = new Set(prev);
          roomsToRemove.forEach((key: string) => newSet.delete(key));
          return newSet;
        });
      }

      // Compute new rooms and zero any stale controller counts in one pass.
      let needsControllerSave = false;
      const newLocalRooms = mergedRooms.map((apiRoom) => {
        // Telegram-style: Only mark as read if user is at bottom of message list
        // If user is scrolled up, keep showing unread count in sidebar
        const isSelectedRoom = selectedRoom?.session_key === apiRoom.session_key;
        const inMarkedSet = markedAsReadRooms.has(apiRoom.session_key);
        const shouldMarkAsRead = (isSelectedRoom && isUserAtBottom) || inMarkedSet;


        if (shouldMarkAsRead) {
          // Keep the controller in sync so localStorage persists the zeroed count.
          // currentRoom in MSRContext is never set by the new ChatDashboard architecture, so messages
          // keep incrementing the controller count even while the user watches — this corrects it.
          const controllerRoom = controller.chatRooms.find(r => r.chatRoomId === apiRoom.session_key);
          if (controllerRoom && controllerRoom.unreadMessageCount > 0) {
            controllerRoom.unreadMessageCount = 0;
            needsControllerSave = true;
          }
          return { ...apiRoom, unread_count: 0 };
        }
        return apiRoom;
      });

      // Persist zeroed counts once so page refresh sees the correct value.
      if (needsControllerSave) {
        controller.saveChatRooms();
      }

      setLocalRooms(newLocalRooms);
    }
  }, [data?.chatRooms, mergeRoomsWithControllerState, selectedRoom?.session_key, markedAsReadRooms, isUserAtBottom]);

  // Mark room as read when user reaches bottom (Telegram-style)
  useEffect(() => {
    if (selectedRoom?.session_key && isUserAtBottom) {
      setMarkedAsReadRooms(prev => new Set(prev).add(selectedRoom.session_key));
    }
  }, [selectedRoom?.session_key, isUserAtBottom]);

  // Track isUserAtBottom in a ref so we can access it when leaving the room
  const wasAtBottomRef = React.useRef<boolean>(true);
  useEffect(() => {
    wasAtBottomRef.current = isUserAtBottom;
  }, [isUserAtBottom]);

  const markedAsReadRoomsRef = React.useRef<Set<string>>(new Set());
  useEffect(() => { markedAsReadRoomsRef.current = markedAsReadRooms; }, [markedAsReadRooms]);

  const selectedRoomKeyRef = React.useRef<string | null>(null);
  useEffect(() => { selectedRoomKeyRef.current = selectedRoom?.session_key ?? null; }, [selectedRoom?.session_key]);

  // When user leaves a room (selectedRoom changes), refetch rooms to get updated unread counts
  const prevSelectedRoomRef = React.useRef<string | null>(null);
  useEffect(() => {
    const prevKey = prevSelectedRoomRef.current;
    const currentKey = selectedRoom?.session_key || null;

    // If we had a previous room and now it's different (left the room)
    if (prevKey && prevKey !== currentKey) {

      // If user was at bottom when leaving, ensure room is marked as read
      // This handles the case where new messages arrived while user was in the chat
      // and the existing effect (lines 96-101) didn't re-trigger because isUserAtBottom didn't change
      if (wasAtBottomRef.current) {
        setMarkedAsReadRooms(prev => new Set(prev).add(prevKey));
        console.log(`[UNREAD] added "${prevKey}" to markedAsReadRooms on room-leave`);

        // Zero the controller's unread count so localStorage reflects what the user actually read.
        // Without this, currentRoom in MSRContext is never updated (it's a legacy field not called
        // in the new ChatDashboard architecture), so messages increment the controller count even
        // while the user is viewing the room. On page refresh markedAsReadRooms resets to empty,
        // so the stale controller count would reappear as a badge.
        const controllerRoom = controller.chatRooms.find(r => r.chatRoomId === prevKey);
        if (controllerRoom && controllerRoom.unreadMessageCount > 0) {
          controllerRoom.unreadMessageCount = 0;
          controller.saveChatRooms();
        }
      }

      // Refetch rooms to get updated unread counts from backend
      // Small delay to allow seen event to be processed by backend
      setTimeout(() => {
        refetch();
      }, 500);
    }

    prevSelectedRoomRef.current = currentKey;
  }, [selectedRoom?.session_key, refetch]);

  const rooms = localRooms;

  const withCompanyDomain = (name: string): string => {
    const normalized = String(name || "").trim();
    if (!normalized) return "";
    return normalized.includes("@") ? normalized : `${normalized}${Global.companyDomain || ""}`;
  };

  // Helper function to get receiver username from participants for agent-to-agent
  const getReceiverUsername = (room: ChatRoom): string => {
    if (room.session_type !== "agent-to-agent") return "";

    const receiver = room.participants?.find((p) => p.is_current_user === false);
    const details = receiver?.details as any;

    if (receiver?.details && isAgentUserType(receiver.user_type)) {
      const rawName =
        details?.agent_username ||
        details?.username ||
        details?.admin_username ||
        details?.display_name ||
        "";
      return withCompanyDomain(rawName);
    }

    const sessionParts = String(room.session_key || "")
      .replace(/^a2a:/, "")
      .split("+")
      .map((p) => p.trim())
      .filter(Boolean);
    const fallbackPeer = sessionParts.find((p) => p !== withCompanyDomain(Global.username));
    return withCompanyDomain(fallbackPeer || "");
  };

  const getPeerUsernameFromRoom = (room: ChatRoom): string => {
    const rawRoomName = String(room.session_key || "")
      .replace(/^a2a:/, "")
      .replace(/^conf:/, "")
      .replace(/^grpcht:/, "");

    const sessionParts = rawRoomName
      .split("+")
      .map((part) => part.trim())
      .filter(Boolean);

    const currentUser = withCompanyDomain(Global.username);
    const peerName = sessionParts.find((part) => part !== currentUser);
    if (peerName) {
      return withCompanyDomain(peerName);
    }

    const fallbackParticipant = room.participants?.find((participant) => participant.is_current_user === false);
    const fallbackDetails = fallbackParticipant?.details as any;
    const fallbackName =
      fallbackDetails?.agent_username ||
      fallbackDetails?.username ||
      fallbackDetails?.admin_username ||
      fallbackDetails?.client_username ||
      fallbackDetails?.display_name ||
      "";

    return withCompanyDomain(fallbackName);
  };

  React.useEffect(() => {
    if (!contextRooms || contextRooms.length === 0) return;

    setLocalRooms((prevRooms) => {
      const baseRooms = prevRooms.length > 0 ? prevRooms : (data?.chatRooms || []);
      const mergedRooms = mergeRoomsWithControllerState(baseRooms);

      // Re-apply read state so unread badges don't reappear for rooms the user already opened.
      const currentSelectedKey = selectedRoomKeyRef.current;
      return mergedRooms.map((room) => {
        const isSelected = currentSelectedKey === room.session_key;
        if ((isSelected && wasAtBottomRef.current) || markedAsReadRoomsRef.current.has(room.session_key)) {
          return { ...room, unread_count: 0 };
        }
        return room;
      });
    });

    // Controller rooms use chatRoomId which equals the API session_key.
    // If the controller has a room that isn't in the latest API data yet, refetch.
    // We compare against data?.chatRooms (a dep) rather than a ref so that after
    // refetch completes data?.chatRooms will contain the new room and this check
    // will return false — breaking the loop naturally without extra bookkeeping.
    const apiKeys = new Set((data?.chatRooms as any[] ?? []).map((r) => r.session_key));
    const hasNewRoom = (contextRooms as any[]).some((r) => r.chatRoomId && !apiKeys.has(r.chatRoomId));
    if (hasNewRoom) {
      refetch();
    }
  }, [contextRooms, data?.chatRooms, mergeRoomsWithControllerState, refetch]);


  // Delete message using MSRP protocol
  const handleDeleteMessage = (messageId: string, _agentUsername?: string) => {
    if (!selectedRoom) return;

    const id = generateRandomString(16);
    const toName = selectedRoom.session_type_id === 3 || selectedRoom.session_type_id === 2
      ? selectedRoom.session_key
      : selectedRoom.session_type === "c2a"
        ? withCompanyDomain(selectedRoom?.client?.client_username || "")
        : selectedRoom.session_type === "agent-to-agent"
          ? getReceiverUsername(selectedRoom)
          : withCompanyDomain(selectedRoom?.agent?.agent_username || "");

    const payload = {
      id,
      type: "request",
      job: "delete-message",
      parameters: {
        "deleted-message-id": messageId,
        "from-name": Global.username + Global.companyDomain,
        "to-name": toName,
        "delete-time": Math.floor(Date.now() / 1000),
      },
    };

    wrapper?.sendMsg(JSON.stringify(payload, null, 4), "application/rechat.cntl+json");

    handleaddServerRequest?.(
      new MyRequest(
        id,
        Global.username + Global.companyDomain,
        toName,
        "delete-message",
        "pending",
        "This message has been deleted",
        false,
        new Date().toISOString(),
        messageId
      )
    );
  };

  // Edit message using MSRP protocol
  const handleEditMessage = (messageId: string, newText: string) => {
    if (!selectedRoom) return;

    const toName =
      selectedRoom.session_type === "c2a"
        ? withCompanyDomain(selectedRoom?.client?.client_username || "") || getPeerUsernameFromRoom(selectedRoom)
        : selectedRoom.session_type === "agent-to-agent"
          ? getReceiverUsername(selectedRoom) || getPeerUsernameFromRoom(selectedRoom)
          : selectedRoom.session_type === "group-chat" || selectedRoom.session_type === "conference"
            ? selectedRoom.session_key || getPeerUsernameFromRoom(selectedRoom)
            : withCompanyDomain(selectedRoom?.agent?.agent_username || "") || getPeerUsernameFromRoom(selectedRoom);

    const multipartMessage = createEmptyMultiPart();
    const reqID = generateRandomString(16);

    const messageJson = {
      id: reqID,
      type: "request",
      job: "edit-message",
      parameters: {
        "current-message-id": messageId,
        "from-name": Global.username + Global.companyDomain,
        "to-name": toName,
        "chat-type": selectedRoom.session_type,
      },
    };

    const messageJsonString = JSON.stringify(messageJson, null, 4);

    const part1 = new MessagePart({
      name: "part1",
      contentType: "application/rechat.cntl+json\r\n",
      filename: "file1.txt",
      value: messageJsonString,
      contentDisposition: "attachment",
    });

    const part2 = new MessagePart({
      name: "part2",
      contentType: "text/plain\r\n",
      filename: "file2.txt",
      value: newText,
      contentDisposition: "attachment",
    });

    multipartMessage.addPart(part1);
    multipartMessage.addPart(part2);

    const Body = multipartMessage.toString();
    const Boundary = multipartMessage.boundary;
    const Type = 'multipart/related;boundary="' + Boundary + '"';

    wrapper?.sendMsg(Body, Type);

    setLocalRooms((prevRooms) =>
      prevRooms.map((candidate) =>
        candidate.session_key === selectedRoom.session_key
          ? { ...candidate, last_message: { ...(candidate.last_message || {}), text: newText } }
          : candidate
      )
    );

    handleaddServerRequest?.(
      new MyRequest(
        reqID,
        Global.username + Global.companyDomain,
        toName,
        "edit-message",
        "pending",
        newText,
        false,
        new Date().toISOString(),
        messageId
      )
    );
  };

  const handleSendMessage = (text: string, _sender: string, _isOwner: boolean, _id: string) => {
    if (!selectedRoom) return;
    const now = Math.floor(Date.now() / 1000);
    const controllerRoom = controller.chatRooms.find((room) => room.chatRoomId === selectedRoom.session_key);
    if (controllerRoom) {
      controller.addMessage(
        new Message(
          text,
          _id,
          true,
          selectedRoom.session_key,
          Global.username + Global.companyDomain,
          null,
          new Date(),
          new Date(),
          [],
          null,
          null,
          new Date()
        ),
        selectedRoom.session_key
      );
      (controllerRoom as any).last_message_at = now;
      controller.saveChatRooms();
    }
    setLocalRooms(prev => prev.map(room =>
      room.session_key === selectedRoom.session_key
        ? { ...room, last_message: { ...(room.last_message || {}), text }, last_message_at: now }
        : room
    ));
  };

  const handleSendFileMessage = (fileMessage: any) => {
    if (!selectedRoom) return;
    const preview = fileMessage.fileName ? `📎 ${fileMessage.fileName}` : "File";
    const now = Math.floor(Date.now() / 1000);
    const controllerRoom = controller.chatRooms.find((room) => room.chatRoomId === selectedRoom.session_key);
    if (controllerRoom) {
      controller.addMessage(
        new FileMessage(
          fileMessage.text || preview,
          fileMessage.fileName || "",
          fileMessage.filePath || "",
          fileMessage.fileType || "",
          fileMessage.messageId,
          true,
          selectedRoom.session_key,
          Global.username + Global.companyDomain,
          fileMessage.repliedTo || null,
          new Date(),
          new Date(),
          new Date(),
          [],
          null,
          null
        ),
        selectedRoom.session_key
      );
      (controllerRoom as any).last_message_at = now;
      controller.saveChatRooms();
    }
    setLocalRooms(prev => prev.map(room =>
      room.session_key === selectedRoom.session_key
        ? { ...room, last_message: { ...(room.last_message || {}), text: preview }, last_message_at: now }
        : room
    ));
  };

  // Flip a room's dot instantly on client-connected/disconnected, without waiting on a rooms refetch
  const handleSessionStatusChange = React.useCallback((sessionKey: string, status: "active" | "inactive") => {
    setLocalRooms(prev => prev.map(room =>
      isSameSessionKey(room.session_key, sessionKey) ? { ...room, session_status: status } : room
    ));
  }, []);

  const handleRoomSelect = (room: ChatRoom | null) => {
    // Handle null (deselection)
    if (!room) {
      setSelectedRoom(null);
      return;
    }

    handlemarkAsRead(room.session_key);
    setSelectedRoom(room);

    // Don't clear unread count immediately!
    // Telegram-style: only clear when user scrolls to bottom (handled by isUserAtBottom effect)
  };

  const handleRegisterClearFunction = (clearFn: () => void) => {
    clearSelectionRef.current = clearFn;
  };

  const handleRegisterSelectFunction = (selectFn: (room: ChatRoom) => void) => {
    selectRoomRef.current = selectFn;
  };

  const handleClearSelection = () => {
    setSelectedRoom(null);

    // Also clear the selection in ConversationsSidebarContainer
    if (clearSelectionRef.current) {
      clearSelectionRef.current();
    }
  };

  const handleAutoSelectRoom = (sessionKey: string) => {

    // Find the room in the rooms list
    const roomToSelect = rooms.find((r: ChatRoom) => r.session_key === sessionKey);

    if (roomToSelect) {

      // Update ChatDashboard's state
      setSelectedRoom(roomToSelect);

      // Also update ConversationsSidebarContainer's state
      if (selectRoomRef.current) {
        selectRoomRef.current(roomToSelect);
      }
    } else {
      // Refetch rooms and try again after a short delay
      refetch().then(() => {
        setTimeout(() => {
          const retryRoom = rooms.find((r: ChatRoom) => r.session_key === sessionKey);
          if (retryRoom) {

            // Update ChatDashboard's state
            setSelectedRoom(retryRoom);

            // Also update ConversationsSidebarContainer's state
            if (selectRoomRef.current) {
              selectRoomRef.current(retryRoom);
            }
          } else {
          }
        }, 300);
      });
    }
  };

  // Check if banner should be shown
  const showBanner =
    context.connectionState === 'reconnecting' ||
    context.connectionState === 'connecting' ||
    context.connectionState === 'offline' ||
    context.connectionState === 'disconnected';

  return (
    <div className="h-screen overflow-hidden bg-gray-50 dark:bg-gray-900">
      <SidebarProvider>
        {/* Connection status banner - fixed at very top */}
        <ConnectionStatusBanner />

        {/* Navbar - below banner when shown */}
        <div className={`fixed ${showBanner ? 'top-10' : 'top-0'} left-0 right-0 z-40 transition-all duration-200`}>
          <Navbar />
        </div>

        {/* Main content - account for navbar + banner */}
        <div className={`flex items-start ${showBanner ? 'pt-[7rem]' : 'pt-16'} transition-all duration-200`}>
          <AppSidebar />

          {/* Main content area with sidebar + chat.
              ml-64 (matches w-64 AppSidebar) clears the fixed-positioned AppSidebar
              on desktop so the chat sidebar isn't covered. */}
          <div className="flex-1 flex h-[calc(100vh-4rem)] w-full md:ml-64">
            <ConversationsSidebarContainer
              rooms={rooms}
              isLoading={isLoading}
              onRoomSelect={handleRoomSelect}
              onClearSelection={handleRegisterClearFunction}
              onRegisterSelectFn={handleRegisterSelectFunction}
            >
              {(selectedRoom) => (
                <ChatAreaChild
                  room={selectedRoom}
                  wrapper={wrapper as any}
                  currentUser={Global.username + Global.companyDomain}
                  onEditMessage={handleEditMessage}
                  onDeleteMessage={handleDeleteMessage}
                  onSendMessage={handleSendMessage}
                  onSendFileMessage={handleSendFileMessage}
                  onRefreshRooms={refetch}
                  onSessionStatusChange={handleSessionStatusChange}
                  onClearSelection={handleClearSelection}
                  onAutoSelectRoom={handleAutoSelectRoom}
                  onScrollPositionChange={setIsUserAtBottom}
                />
              )}
            </ConversationsSidebarContainer>
          </div>
        </div>
      </SidebarProvider>
    </div>
  );
};

export default ChatDashboard;
